import { ProcessEnhanceTextDocument, RichTextEditorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

const useRichTextEditor = () => {
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(RichTextEditorDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const canUseRichTextEditor =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_ats_rich_text_editor'
    )?.enabled ?? false

  const canUseEnhanceAI =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_rich_text_editor_ai'
    )?.enabled ?? false

  const defaultError = i18n.t('rich_text_editor.ai.defaultError')

  const prompts = data?.openAi.editorPromptsConnection.nodes || []

  const enhancementOptions = prompts.map(({ option, suboptions }) => ({
    id: option.name,
    label: option.label,
    subOptions: suboptions?.map(({ name, label }) => ({
      id: name,
      label: label,
    })),
  }))

  const [processEnhanceText] = useMutation(ProcessEnhanceTextDocument, {
    extractorFn: (result) => result.openAiMutations.processOpenaiEnhancedText,
  })

  const onEnhanceText = async (params: {
    text: string
    selectedIntent?: string
    customIntent?: string
    context?: string
  }): Promise<{
    success: boolean
    text: string
    error?: string
  }> => {
    const { text, selectedIntent, customIntent } = params

    const result = await processEnhanceText({
      variables: {
        companyId: company.id,
        text,
        selectedIntent,
        customIntent,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success && handled.data?.enhancedText) {
      if (handled.data?.enhancedText?.success) {
        return {
          success: true,
          text: handled.data.enhancedText.text || '',
        }
      }
      return {
        success: false,
        text,
        error: handled.data?.enhancedText?.error || defaultError,
      }
    }

    return {
      success: false,
      text,
      error: defaultError,
    }
  }

  const enhanceConfig = {
    onEnhanceText,
    enhancementOptions,
  }

  const plainHtmlMode = true

  const editorConfig = canUseEnhanceAI ? { enhanceConfig, plainHtmlMode } : { plainHtmlMode }

  return { editorConfig, canUseRichTextEditor, loading }
}

export default useRichTextEditor
